// Radha — invoice discounting (TReDS / bank factoring) tracker, surfaced
// inside Invoicing. Not an API integration — RXIL/M1xchange/Invoicemart have
// no public developer API, and real participation needs the tenant's own
// customers to also be registered on the same exchange. This logs what
// happens on that external portal so Radha's own AR bookkeeping stays right.
import React, { useState } from "react";

import {
  useInvoiceDiscountingList, useSubmitForDiscounting, useUpdateDiscounting, discountingErrorMessage,
  type DiscountingPlatform, type DiscountingStatus,
} from "../hooks/useInvoiceDiscounting";
import type { Invoice } from "../hooks/useBatchModules";
import { fmtMoney as inr, homeCurrency, fmtDate } from "../lib/money";


const PLATFORM_LABEL: Record<DiscountingPlatform, string> = {
  treds_rxil: "TReDS (RXIL)", treds_m1xchange: "TReDS (M1xchange)", treds_invoicemart: "TReDS (Invoicemart)",
  bank_factoring: "Bank factoring", other: "Other",
};
const STATUS_COLOR: Record<DiscountingStatus, string> = {
  submitted: "var(--fg-muted)", bid_received: "var(--status-warning)",
  discounted: "var(--status-good)", settled: "var(--status-good)", rejected: "var(--status-critical)",
};
const STATUSES: DiscountingStatus[] = ["submitted", "bid_received", "discounted", "settled", "rejected"];

function SubmitForm({ invoices, canWrite }: { invoices: Invoice[]; canWrite: boolean }) {
  const submit = useSubmitForDiscounting();
  const [invoiceId, setInvoiceId] = useState("");
  const [platform, setPlatform] = useState<DiscountingPlatform>("treds_rxil");
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  if (!canWrite) return null;

  const go = () => {
    if (!invoiceId) return;
    setNote(null);
    submit.mutate({ invoice_id: invoiceId, platform }, {
      onSuccess: () => { setNote({ ok: true, text: "Logged — track its progress on " + PLATFORM_LABEL[platform] + " itself, update status here" }); setInvoiceId(""); },
      onError: (e) => setNote({ ok: false, text: discountingErrorMessage(e) }),
    });
  };

  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
      <select value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)}
        style={{ padding: "6px 9px", border: "1px solid var(--border)", borderRadius: 7, fontSize: 13, background: "var(--surface)", color: "var(--fg)", minWidth: 260 }}>
        <option value="">Select an invoice to log…</option>
        {invoices.map((i) => <option key={i.id} value={i.id}>{i.invoice_number} · {i.customer_name} · {inr(Number(i.total_amount))}</option>)}
      </select>
      <select value={platform} onChange={(e) => setPlatform(e.target.value as DiscountingPlatform)}
        style={{ padding: "6px 9px", border: "1px solid var(--border)", borderRadius: 7, fontSize: 13, background: "var(--surface)", color: "var(--fg)" }}>
        {Object.entries(PLATFORM_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      <button onClick={go} disabled={!invoiceId || submit.isPending}
        style={{ padding: "6px 14px", background: "var(--accent-purple)", color: "#fff", border: 0, borderRadius: 8, cursor: "pointer", fontSize: 13, opacity: !invoiceId || submit.isPending ? 0.5 : 1 }}>
        {submit.isPending ? "Logging…" : "Log submission"}
      </button>
      {note && <span style={{ fontSize: 12, color: note.ok ? "var(--status-good)" : "var(--status-critical)" }}>{note.text}</span>}
    </div>
  );
}

function Row({ d, invoiceLabel, canWrite }: { d: import("../hooks/useInvoiceDiscounting").InvoiceDiscounting; invoiceLabel: string; canWrite: boolean }) {
  const update = useUpdateDiscounting();
  const [rate, setRate] = useState(d.discount_rate_pct ?? "");
  const [net, setNet] = useState(d.net_amount_received ?? "");

  return (
    <tr style={{ borderTop: "1px solid var(--border)" }}>
      <td style={{ padding: "8px" }}>{invoiceLabel}</td>
      <td style={{ padding: "8px", fontSize: 12, color: "var(--fg-muted)" }}>{PLATFORM_LABEL[d.platform]}</td>
      <td style={{ padding: "8px" }}>
        {canWrite ? (
          <select value={d.status} onChange={(e) => update.mutate({ id: d.id, status: e.target.value as DiscountingStatus })}
            style={{ padding: "2px 6px", fontSize: 12, border: `1px solid ${STATUS_COLOR[d.status]}`, color: STATUS_COLOR[d.status], background: "none", borderRadius: 5 }}>
            {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
        ) : <span style={{ textTransform: "capitalize", color: STATUS_COLOR[d.status] }}>{d.status.replace(/_/g, " ")}</span>}
      </td>
      <td style={{ padding: "8px" }}>
        {canWrite ? (
          <input type="number" value={rate} placeholder="rate %" onChange={(e) => setRate(e.target.value)}
            onBlur={() => rate !== (d.discount_rate_pct ?? "") && update.mutate({ id: d.id, discount_rate_pct: Number(rate) || undefined })}
            style={{ width: 64, padding: "2px 6px", fontSize: 12, border: "1px solid var(--border)", borderRadius: 5, background: "var(--surface)", color: "var(--fg)" }} />
        ) : (d.discount_rate_pct ? `${d.discount_rate_pct}%` : "—")}
      </td>
      <td style={{ padding: "8px" }}>
        {canWrite ? (
          <input type="number" value={net} placeholder={`net (${homeCurrency()})`} onChange={(e) => setNet(e.target.value)}
            onBlur={() => net !== (d.net_amount_received ?? "") && update.mutate({ id: d.id, net_amount_received: Number(net) || undefined })}
            style={{ width: 90, padding: "2px 6px", fontSize: 12, border: "1px solid var(--border)", borderRadius: 5, background: "var(--surface)", color: "var(--fg)" }} />
        ) : (d.net_amount_received ? inr(Number(d.net_amount_received)) : "—")}
      </td>
      <td style={{ padding: "8px", fontSize: 11, color: "var(--fg-faint)" }}>{fmtDate(d.submitted_at)}</td>
    </tr>
  );
}

export function InvoiceDiscounting({ invoices, canWrite }: { invoices: Invoice[]; canWrite: boolean }) {
  const { data: list, isLoading } = useInvoiceDiscountingList();
  const invoiceLabel = (id: string) => {
    const inv = invoices.find((i) => i.id === id);
    return inv ? `${inv.invoice_number} · ${inv.customer_name}` : id;
  };
  const eligible = invoices.filter((i) => i.status !== "paid" && !(list ?? []).some((d) => d.invoice_id === i.id));

  return (
    <div className="sanctuary-card" style={{ padding: "14px 16px", marginBottom: 22 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
        <div style={{ font: "600 15px Inter" }}>Invoice discounting</div>
      </div>
      <p style={{ color: "var(--fg-muted)", fontSize: 12, margin: "0 0 12px" }}>
        Get paid early against a receivable via TReDS or bank factoring. The actual bid/discount happens on that
        platform's own portal — log it here so it stays visible next to the rest of your AR.
      </p>
      <SubmitForm invoices={eligible} canWrite={canWrite} />
      {isLoading ? (
        <p style={{ color: "var(--fg-muted)", fontSize: 13 }}>Loading…</p>
      ) : (list ?? []).length === 0 ? (
        <p style={{ color: "var(--fg-muted)", fontSize: 13 }}>No invoices submitted for discounting yet.</p>
      ) : (
        <div className="sanctuary-scroll-x">
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left", color: "var(--fg-muted)", fontSize: 11.5 }}>
                <th style={{ padding: "4px 8px", fontWeight: 500 }}>Invoice</th>
                <th style={{ padding: "4px 8px", fontWeight: 500 }}>Platform</th>
                <th style={{ padding: "4px 8px", fontWeight: 500 }}>Status</th>
                <th style={{ padding: "4px 8px", fontWeight: 500 }}>Rate</th>
                <th style={{ padding: "4px 8px", fontWeight: 500 }}>Net received</th>
                <th style={{ padding: "4px 8px", fontWeight: 500 }}>Submitted</th>
              </tr>
            </thead>
            <tbody>
              {(list ?? []).map((d) => <Row key={d.id} d={d} invoiceLabel={invoiceLabel(d.invoice_id)} canWrite={canWrite} />)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
